/**
 * Authentication Store - Manage user authentication state
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/** Úroveň prístupu k modulu. Chýbajúci záznam = modul nemá vôbec. */
export type ModuleAccess = 'read' | 'edit';

const PORADIE_UROVNI: Record<ModuleAccess, number> = { read: 1, edit: 2 };

export type UserRole = 'admin' | 'engineer' | 'service_tech' | 'install_tech' | 'viewer' | 'guest';

export interface User {
  id: number;
  email: string;
  username: string;
  full_name?: string;
  role: UserRole;
  is_active: boolean;
  is_verified: boolean;
  created_at: string;
  updated_at?: string;
  last_login?: string;
  modules?: string[];  // List of module names user has access to
  /** Úroveň na jednotlivých moduloch: { customer_management: 'edit', ... } */
  module_levels?: Record<string, ModuleAccess>;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  
  // Actions
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  setUser: (user: User, token: string) => void;
  checkAuth: () => Promise<void>;
  
  // Permission checks
  isAdmin: () => boolean;
  isEngineer: () => boolean;
  canEditProjects: () => boolean;
  canManageUsers: () => boolean;
  hasModule: (moduleName: string, level?: ModuleAccess) => boolean;
  moduleAccess: (moduleName: string) => ModuleAccess | null;
}

const API_URL = process.env.REACT_APP_API_URL && process.env.REACT_APP_API_URL !== '' ? process.env.REACT_APP_API_URL : '/api';

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: localStorage.getItem('token') || null,
      isAuthenticated: false,

      login: async (email: string, password: string) => {
        try {
          const formData = new FormData();
          formData.append('username', email); // OAuth2 uses 'username' field
          formData.append('password', password);

          const url = `${API_URL}/auth/login`;
          console.log('[AuthStore] Login URL:', url);

          const response = await fetch(url, {
            method: 'POST',
            body: formData,
          });

          if (!response.ok) {
            const error = await response.json();
            throw new Error(error.detail || 'Prihlásenie zlyhalo');
          }

          const data = await response.json();
          console.log('[AuthStore] Login successful, token:', data.access_token ? 'received' : 'missing');
          console.log('[AuthStore] User data:', data.user);

          // Store token in localStorage for API calls
          if (data.access_token) {
            localStorage.setItem('token', data.access_token);
            console.log('[AuthStore] Token stored in localStorage');
          }

          set({
            user: data.user,
            token: data.access_token,
            isAuthenticated: true,
          });

          console.log('[AuthStore] State updated, checking localStorage...');
          setTimeout(() => {
            const stored = localStorage.getItem('auth-storage');
            const token = localStorage.getItem('token');
            console.log('[AuthStore] localStorage auth-storage:', stored ? 'exists' : 'missing');
            console.log('[AuthStore] localStorage token:', token ? 'exists' : 'missing');
          }, 100);
        } catch (error) {
          console.error('[AuthStore] Login error:', error);
          throw error;
        }
      },

      logout: () => {
        // Clear localStorage
        localStorage.removeItem('token');
        localStorage.removeItem('auth-storage');

        set({
          user: null,
          token: null,
          isAuthenticated: false,
        });
      },

      setUser: (user: User, token: string) => {
        set({
          user,
          token,
          isAuthenticated: true,
        });
      },

      checkAuth: async () => {
        const { token, user } = get();

        console.log('[AuthStore] checkAuth called');
        console.log('[AuthStore] - token:', token ? `exists (${token.substring(0, 20)}...)` : 'missing');
        console.log('[AuthStore] - user:', user?.email || 'missing');
        console.log('[AuthStore] - localStorage token:', localStorage.getItem('token') ? 'exists' : 'missing');
        console.log('[AuthStore] - localStorage auth-storage:', localStorage.getItem('auth-storage') ? 'exists' : 'missing');

        if (!token) {
          console.log('[AuthStore] No token, setting isAuthenticated to false');
          set({ isAuthenticated: false, user: null });
          return;
        }

        // If we already have a user and token, just verify the token is still valid
        // Don't immediately logout on error - could be network issue
        try {
          const url = `${API_URL}/auth/me`;
          console.log('[AuthStore] Fetching:', url);
          console.log('[AuthStore] - Authorization header:', `Bearer ${token.substring(0, 20)}...`);

          const response = await fetch(url, {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });

          console.log('[AuthStore] Response status:', response.status);

          if (!response.ok) {
            const errorText = await response.text();
            console.log('[AuthStore] Error response:', errorText);

            // Only logout on 401 Unauthorized (invalid token)
            // Keep user logged in for other errors (network, 500, etc.)
            if (response.status === 401) {
              console.log('[AuthStore] Token invalid (401), logging out');
              set({ isAuthenticated: false, user: null, token: null });
              localStorage.removeItem('token');
              localStorage.removeItem('auth-storage');
            } else {
              console.warn('[AuthStore] Auth check failed with status', response.status, 'but keeping user logged in');
              // Keep existing user and token
              if (user) {
                set({ isAuthenticated: true });
              }
            }
            return;
          }

          const userData = await response.json();
          console.log('[AuthStore] Auth check successful, user:', userData.email);
          set({ user: userData, isAuthenticated: true });
        } catch (error) {
          console.error('[AuthStore] Auth check error:', error);
          // On network error, keep user logged in if we have user data
          if (user) {
            console.warn('[AuthStore] Network error, but keeping user logged in');
            set({ isAuthenticated: true });
          } else {
            console.log('[AuthStore] Network error and no user data, logging out');
            set({ isAuthenticated: false, user: null, token: null });
            localStorage.removeItem('token');
            localStorage.removeItem('auth-storage');
          }
        }
      },

      // Permission checks
      isAdmin: () => {
        const { user } = get();
        return user?.role === 'admin';
      },

      isEngineer: () => {
        const { user } = get();
        return user?.role === 'engineer' || user?.role === 'admin';
      },

      canEditProjects: () => {
        const { user } = get();
        return user?.role === 'admin' || user?.role === 'engineer';
      },

      canManageUsers: () => {
        const { user } = get();
        return user?.role === 'admin';
      },

      moduleAccess: (moduleName: string): ModuleAccess | null => {
        const { user } = get();
        if (!user) return null;
        // Admins have access to all modules by default
        if (user.role === 'admin') return 'edit';
        // Technicians have pole notes by default (mirrors backend User.has_module)
        if (
          (user.role === 'service_tech' || user.role === 'install_tech') &&
          moduleName === 'pole_notes'
        ) {
          return 'edit';
        }
        // Úrovne posiela backend. Starší token ich ešte nemá, tam platí, že
        // pridelený modul znamenal "smie používať" — čiže úprava.
        const uroven = user.module_levels?.[moduleName];
        if (uroven) return uroven;
        return user.modules?.includes(moduleName) ? 'edit' : null;
      },

      hasModule: (moduleName: string, level: ModuleAccess = 'read') => {
        const mam = get().moduleAccess(moduleName);
        if (!mam) return false;
        return PORADIE_UROVNI[mam] >= PORADIE_UROVNI[level];
      },
    }),
    {
      name: 'auth-storage',
    }
  )
);
